// The form config behind a custom document template that has no generator of
// its own (a business's legal agreement or blank-canvas design, an admin's
// custom-category template). It runs through the same config-driven modal the
// legal and business forms use: card sections of Ionic inputs, then the
// preview modal with the download. The fields come from the tokens the layout
// actually uses, grouped by the categories its designer gave them.
import { extractLayoutTokens, extractLayoutTables, getCustomFields, renderLayout, fetchPublishedLayout } from "./layoutEngine";
import { loadJsPDF } from "./pdfEngines";
import { pdfToPageImages } from "./pdfPageImages";
import { addPreviewWatermarkJsPdf } from "./previewWatermark";

// "annualSalary" -> "Annual Salary"; "partyAName" -> "Party A Name"
export function tokenLabel(token) {
  const last = String(token || "").split(".").pop();
  return last
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || token;
}

const isLongText = (t) => /terms|notes|description|recitals|body|paragraph|message|summary/i.test(t);
const isDateish = (t) => /date\b|Date$/i.test(t);

// The raw {token: value} map the layout renders from: every field as typed,
// plus the flags a showIf tests: "hasRecitals" follows whether {recitals} was
// filled in (so an optional block hides when empty, instead of the sample
// flag the blank merge brings), and a signature or logo image sets its own.
// Every token the layout uses starts blank, so a field the customer never
// touched renders empty rather than the sample text the blank merge carries.
// A cleared value is written as "" rather than null, since the raw context
// skips null and would keep the old one.
function layoutValues(fd, layout) {
  const out = {};
  for (const t of (layout ? extractLayoutTokens(layout) : [])) { out[t] = ""; out[`has${t[0].toUpperCase()}${t.slice(1)}`] = ""; }
  for (const [k, v] of Object.entries(fd || {})) {
    if (k === "__template") continue;
    out[k] = v === null || v === undefined ? "" : v;
    if (typeof out[k] === "string") out[`has${k[0].toUpperCase()}${k.slice(1)}`] = out[k].trim() ? "true" : "";
  }
  for (const p of ["A", "B"]) out[`hasParty${p}SignatureImage`] = fd && fd[`party${p}Signature`] ? "true" : "";
  out.hasLogo = fd && fd.logoDataUrl ? "true" : "";
  return out;
}

function sectionsFor(layout, variants) {
  const sections = [];
  // several styles of one document switch here; what was typed carries over
  if (variants.length > 1) {
    sections.push({ title: "Template", fields: [
      { name: "__template", label: "Template", type: "select", size: "12", options: variants.map((v) => ({ value: v.id, label: v.name })) },
    ] });
  }
  if (!layout) return sections;

  const byToken = {};
  for (const f of getCustomFields(layout)) if (f && f.token) byToken[f.token] = f;
  // the designer's categories become the sections, in the order they listed
  // them; tokens the template never described go under Details, last
  const groups = [];
  const groupFor = (title) => {
    let g = groups.find((x) => x.title === title);
    if (!g) { g = { title, fields: [] }; groups.push(g); }
    return g;
  };
  for (const f of getCustomFields(layout)) if (f && String(f.category || "").trim()) groupFor(String(f.category).trim());
  const details = groupFor("Details");
  for (const token of extractLayoutTokens(layout)) {
    const fld = byToken[token];
    const type = fld ? (fld.type || "text") : (isLongText(token) ? "textarea" : isDateish(token) ? "date" : "text");
    const field = { name: token, label: (fld && fld.label) || tokenLabel(token), size: type === "textarea" ? "12" : "6" };
    if (type === "textarea" || type === "date" || type === "number" || type === "color") field.type = type;
    if (type === "color") field.default = (fld && fld.default) || "#14532d";
    if (fld && fld.description) field.note = fld.description;
    groupFor((fld && String(fld.category || "").trim()) || "Details").fields.push(field);
  }
  sections.push(...groups.filter((g) => g !== details && g.fields.length));
  if (details.fields.length) sections.push(details);

  for (const tbl of extractLayoutTables(layout)) {
    const n = tbl.columns.length;
    sections.push({ title: tokenLabel(tbl.binding), fields: [{
      name: tbl.binding, type: "rowList", size: "12", addLabel: "Add Row", newRow: () => ({}),
      columns: tbl.columns.map((c) => ({ name: c.key, label: c.header || tokenLabel(c.key), size: String(Math.max(3, Math.floor(12 / n))), sizeSm: "6" })),
    }] });
  }

  // the same signature and logo areas the built-in generators use: the Saved
  // select (Professional/Business), then draw or upload. Neither is a text
  // token, so both are detected in the layout itself.
  let s = "";
  try { s = JSON.stringify(layout); } catch { s = ""; }
  const parties = ["A", "B"].filter((p) => s.includes(`party${p}Signature`));
  if (parties.length) {
    sections.push({ title: "Signatures", fields: parties.map((p) => ({
      name: `party${p}Signature`, label: `Party ${p} signature`, savedLabel: `Saved party ${p} signature`, type: "signature", size: "12",
    })) });
  }
  if (/logoDataUrl|hasLogo/.test(s)) {
    sections.push({ title: "Logo", fields: [{ name: "logoDataUrl", label: "Logo", savedLabel: "Saved logo", type: "image", size: "12" }] });
  }
  return sections;
}

// slug: the category page (also the order's documentType); template: the one
// tapped; variants: every style in its form group; layouts: templateId ->
// published layout (loaded by the category page); included: a business's own
// template rides its plan, so it is never priced.
export function buildCustomDocFormConfig({ slug, template, variants, layouts, groupKey, groupName, templateRef, price, included }) {
  const variantOf = (fd) => variants.find((v) => v.id === (fd && fd.__template)) || template;
  const layoutFor = async (v) => layouts[v.id] || fetchPublishedLayout(v.id, undefined, { user: !!v._own });
  return {
    key: `custom-doc:${groupKey}`,
    docType: slug,
    title: groupName,
    storageKey: `appCustomDocFormData:${groupKey}`,
    included: !!included,
    price: (fd) => price(variantOf(fd)),
    derive: (fd) => ({ __template: template.id, ...(fd || {}) }),
    validate: () => null,
    checkoutTemplate: (fd) => templateRef(variantOf(fd)),
    // what /payment-success renders, the same shape the fill-in modal always sent
    buildPending: (fd) => {
      const v = variantOf(fd);
      return { pendingCustomDocData: { templateId: v.id, templateName: v.name, values: layoutValues(fd, layouts[v.id]), own: !!v._own } };
    },
    preview: async (fd) => {
      const v = variantOf(fd);
      const layout = await layoutFor(v);
      if (!layout) return null;
      const doc = new (await loadJsPDF())({ unit: "pt", format: "letter" });
      renderLayout(doc, layout, layoutValues(fd, layout), slug, { raw: true });
      await addPreviewWatermarkJsPdf(doc, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight());
      return pdfToPageImages(doc.output("arraybuffer"));
    },
    sections: (fd) => sectionsFor(layouts[variantOf(fd).id] || null, variants),
  };
}
